import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { CACHED_TIME } from "@/constants/common";
import { useDebounce } from "@/hooks/useDebounce";
import { TOption } from "@/types/common";

export type AsyncSearchPage<T> = { items: T[]; total?: number; nextPage?: number };

export type AsyncSearchConfig<T> = {
  /** Base query key; the hook appends "infinite" + the effective search term. Keep the config stable (module-level). */
  queryKey: readonly unknown[];
  /** Fetch one page. Page 1 omits page/perPage (BE default); next pages send them. */
  fetchPage: (params: { page?: number; perPage?: number; search?: string }) => Promise<AsyncSearchPage<T>>;
  /** Map a raw item to a dropdown option (its `value` is also the dedup/identity key). */
  toOption: (item: T) => TOption;
  perPage?: number;
};

/**
 * The exact cache key this hook uses for a given base `queryKey` + search term. Exported so a caller that wants
 * to seed this hook's cache directly (via `queryClient.setQueryData`, e.g. from a batched fetch shared across
 * several dropdowns) targets the right entry — `initialData` can't be used for this because React Query only
 * applies it the first time a query is created, and this hook's query already exists by the time such data arrives.
 */
export const buildAsyncSearchQueryKey = (queryKey: readonly unknown[], search = "") => [...queryKey, "infinite", search];

const DEFAULT_PER_PAGE = 50;

/**
 * Generic async-search list for paginated dropdowns (used by ComboboxMultiAsync). Lazy: page 1 on open,
 * next pages only on scroll. While not fully loaded the keyword hits the server; once the whole list has
 * been loaded, `allLoaded` flips and search is done client-side.
 */
export function useSearchList<T>({ queryKey, fetchPage, toOption, perPage = DEFAULT_PER_PAGE }: AsyncSearchConfig<T>, enabled = true) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  useDebounce(() => setSearch(input), [input]);

  const [allLoaded, setAllLoaded] = useState(false);
  const effectiveSearch = allLoaded ? "" : search;

  const query = useInfiniteQuery({
    queryKey: buildAsyncSearchQueryKey(queryKey, effectiveSearch),
    queryFn: ({ pageParam }): Promise<AsyncSearchPage<T>> => {
      const page = pageParam as number;
      return fetchPage({
        ...(page > 1 ? { page, perPage } : {}),
        ...(effectiveSearch ? { search: effectiveSearch } : {}),
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: CACHED_TIME,
    gcTime: 0, // drop cache on close → each reopen refetches page 1 only
    refetchOnMount: false, // never refetch all accumulated pages on remount
    enabled,
  });

  const options = useMemo<TOption[]>(() => {
    const seen = new Set<TOption["value"]>();
    const result: TOption[] = [];
    (query.data?.pages ?? []).forEach((page) =>
      page.items.forEach((item) => {
        const opt = toOption(item);
        if (seen.has(opt.value)) return;
        seen.add(opt.value);
        result.push(opt);
      }),
    );
    return result;
  }, [query.data, toOption]);

  // Latch once the UNFILTERED list is fully loaded (no more pages) → switch to client-side filtering.
  // Uses hasNextPage (not `total`) so it works even if the API omits a total count. Latch is write-once.
  useEffect(() => {
    if (!allLoaded && effectiveSearch === "" && query.data != null && !query.hasNextPage) {
      setAllLoaded(true);
    }
  }, [allLoaded, effectiveSearch, query.data, query.hasNextPage]);

  return {
    options,
    allLoaded,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    onInputChange: setInput,
  };
}
